import React, { useMemo } from 'react';
import './FoodBackground.css';

const FloatingIngredient = React.memo(({ x, y, delay, type }: { 
  x: number; 
  y: number;
  delay: number;
  type: string;
}) => (
  <div 
    className={`floating-ingredient ingredient-${type}`}
    style={{
      '--ingredient-x': x,
      '--ingredient-y': y,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const KitchenShelf = React.memo(({ x, y, width }: { 
  x: number; 
  y: number;
  width: number;
}) => (
  <div 
    className="kitchen-shelf"
    style={{
      left: `${x}%`,
      bottom: `${y}%`,
      width: `${width}%`
    }}
  />
));

const FoodBackground = React.memo(() => {
  const ingredients = useMemo(() => 
    Array.from({ length: 12 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 8,
      type: ['flour', 'sugar', 'spice', 'steam'][Math.floor(Math.random() * 4)]
    })),
  []);

  const shelves = useMemo(() => 
    Array.from({ length: 3 }).map((_, i) => ({
      id: i,
      x: 10 + Math.random() * 20,
      y: 25 + i * 20 + Math.random() * 10,
      width: 25 + Math.random() * 15
    })),
  []);

  return (
    <div className="food-background">
      {/* Kitchen gradient */}
      <div className="food-kitchen" />
      
      {/* Oven/stove */}
      <div className="kitchen-oven">
        <div className="oven-body" />
        <div className="oven-door" />
        <div className="oven-window" />
        <div className="oven-burner burner-1" />
        <div className="oven-burner burner-2" />
      </div>
      
      {/* Kitchen counter */}
      <div className="kitchen-counter" />
      
      {/* Shelves */}
      <div className="shelves-layer">
        {shelves.map(shelf => (
          <KitchenShelf
            key={shelf.id}
            x={shelf.x}
            y={shelf.y}
            width={shelf.width}
          />
        ))}
      </div>
      
      {/* Kitchen utensils */}
      <div className="kitchen-utensils">
        <div className="utensil spoon" />
        <div className="utensil fork" />
        <div className="utensil knife" />
        <div className="utensil spatula" />
      </div>
      
      {/* Hanging pots */}
      <div className="hanging-pots">
        <div className="pot pot-1" />
        <div className="pot pot-2" />
        <div className="pot pot-3" />
      </div>
      
      {/* Floating ingredients */}
      <div className="ingredients-layer">
        {ingredients.map(ingredient => (
          <FloatingIngredient
            key={ingredient.id}
            x={ingredient.x}
            y={ingredient.y}
            delay={ingredient.delay}
            type={ingredient.type}
          />
        ))}
      </div>
      
      {/* Recipe papers */}
      <div className="recipe-papers">
        <div className="recipe-paper paper-1" />
        <div className="recipe-paper paper-2" />
      </div>
      
      {/* Kitchen warmth particles */}
      <div className="food-particles" />
      
      {/* Window light */}
      <div className="kitchen-window">
        <div className="window-frame" />
        <div className="window-light" />
      </div>
    </div>
  );
});

export default FoodBackground;